// External Dependencies
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import DomainIcon from '@mui/icons-material/Domain';
import Typography from '@mui/material/Typography';

// Internal Dependencies
import {
  BackToButton,
  DetailCard,
} from 'components/shared';
import { isMobileOrTabletScreenSize } from 'state/selectors';
import { tableQueryParams } from 'state/table/selectors';
import { useGetDistrictById } from 'gql/queries/districts';
import { useIsOpen } from 'hooks/useIsOpen';
import { useIsPrestoOwner } from 'hooks/useIsPrestoOwner';
import Notes from 'components/shared/Notes';

// Local Dependencies
import DistrictDangerZone from './DistrictDangerZone';
import DistrictInfoContent from './DistrictInfoContent';
import VerifyDistrictDialog from './VerifyDistrictDialog';
import DistrictLicensesCard from './DistrictLicensesCard';

// Component Definition
const DistrictShow = (): JSX.Element | null => {
  const { id } = useParams();

  const isMobileOrTabletScreen = useSelector(isMobileOrTabletScreenSize);

  const districtsParams = useSelector(tableQueryParams('admin_portals'));

  const isPrestoOwner = useIsPrestoOwner();

  const {
    isOpen: isVerifyDistrictDialogOpen,
    toggleIsOpen: toggleVerifyDistrictDialogIsOpen,
  } = useIsOpen();

  const { data } = useGetDistrictById(id);

  if (!data) {
    return null;
  }

  const { districtById } = data;
  const isDistrictVerified = districtById.isVerified;

  const subheader = (
    <Box
      display={isMobileOrTabletScreen ? 'block' : 'flex'}
      justifyContent="space-between"
      marginY={1}
    >
      <div>
        <Typography color="textPrimary">{districtById.addressOne}</Typography>
        <Typography color="textPrimary">{districtById.city}, {districtById.state.label} {districtById.zipcode}</Typography>
        <Typography color="textPrimary">{districtById.phoneNumber}</Typography>
      </div>

      <Collapse in={isPrestoOwner && !isDistrictVerified}>
        <Button
          onClick={toggleVerifyDistrictDialogIsOpen}
          size={isMobileOrTabletScreen ? 'small' : 'medium'}
          startIcon={<ThumbUpIcon />}
          variant="outlined"
        >
          Verify Admin Portal
        </Button>
      </Collapse>
    </Box>
  );

  return (
    <>
      <Box marginBottom={3}>
        <BackToButton
          context="All Admin Portals"
          navigateTo={`/admin_portals${districtsParams}`}
        />
      </Box>

      <DetailCard
        avatarAriaLabel="Admin Portal info"
        avatarBackgroundColor={600}
        avatarIcon={<DomainIcon />}
        content={(
          <DistrictInfoContent
            district={districtById}
          />
        )}
        subheader={subheader}
        title={districtById.label}
      />

      <DistrictLicensesCard
        adminPortalId={districtById.id}
        licensePriceInCents={districtById.licensePriceInCents}
      />

      {id && (
        <Box sx={{ marginTop: 2 }}>
          <Notes
            idRef={id}
            tableRef="districts"
          />
        </Box>
      )}

      {isPrestoOwner && (
        <DistrictDangerZone
          adminPortalId={districtById.id}
          districtName={districtById.label}
        />
      )}

      <VerifyDistrictDialog
        adminPortalId={districtById.id}
        onClose={toggleVerifyDistrictDialogIsOpen}
        open={isVerifyDistrictDialogOpen}
      />
    </>
  );
};

export default DistrictShow;
